import React, { useState, useEffect, useRef } from "react";
import { firebase } from "@/api/firebaseAdapter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Plus, Trash2, Mail, Loader2, Copy, Check, PlusCircle, Bell, Lock, Unlock, ExternalLink, Printer } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { Textarea } from "@/components/ui/textarea";
import ProductoDialog from "../productos/ProductoDialog";
import CrearActividadDialog from "../actividades/CrearActividadDialog";
import TruyolSelector from "./TruyolSelector";

const PresupuestoGenerator = React.forwardRef(({ leadId, lead, presupuestoParaCargar }, ref) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [lineas, setLineas] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [textoCopied, setTextoCopied] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [presupuestoActual, setPresupuestoActual] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentUserEmail, setCurrentUserEmail] = useState("");
  const [cargandoPresupuesto, setCargandoPresupuesto] = useState(false);
  const [showProductoDialog, setShowProductoDialog] = useState(false);
  const [categoriaParaNuevoProducto, setCategoriaParaNuevoProducto] = useState("");
  const [lineaParaNuevoProducto, setLineaParaNuevoProducto] = useState(null);
  const [creandoProducto, setCreandoProducto] = useState(false);
  const [textoPresupuesto, setTextoPresupuesto] = useState("");
  const [showActividadDialog, setShowActividadDialog] = useState(false);
  const [creandoActividad, setCreandoActividad] = useState(false);
  const [seguimientoAutomatico, setSeguimientoAutomatico] = useState(true);
  const [truyolAbierto, setTruyolAbierto] = useState(null); // ID de línea con el panel abierto

  useEffect(() => {
    const checkUser = async () => {
      const user = await firebase.auth.me();
      setIsAdmin(user.role === 'admin');
      setCurrentUserEmail(user.email);
    };
    checkUser();
  }, []);

  const { data: categorias = [] } = useQuery({
    queryKey: ['categorias'],
    queryFn: () => firebase.entities.Categoria.filter({ activa: true }),
  });

  const { data: productos = [] } = useQuery({
    queryKey: ['productos'],
    queryFn: () => firebase.entities.Producto.filter({ activo: true }),
  });

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => firebase.entities.Settings.list(),
  });

  const plantillaPresupuesto = settings.find(s => s.clave === 'plantilla_presupuesto')?.valor || "";
  const firmaEmail = settings.find(s => s.clave === 'firma_email')?.valor || "";

  const { data: catalogoTruyol = [] } = useQuery({
    queryKey: ['catalogo_truyol'],
    queryFn: () => firebase.entities.CatalogoTruyol.list(),
    staleTime: 1000 * 60 * 60, // 1 hora — el catálogo no cambia a menudo
  });

  const { data: catalogoTruyolMaestro = [] } = useQuery({
    queryKey: ['catalogo_truyol_maestro'],
    queryFn: () => firebase.entities.CatalogoTruyolMaestro.list(),
    staleTime: 1000 * 60 * 60, // 1 hora — lista maestra
  });

  useEffect(() => {
    setTextoPresupuesto(generarTextoPresupuesto());
  }, [lineas, lead, plantillaPresupuesto, firmaEmail]);

  const { data: presupuestos = [] } = useQuery({
    queryKey: ['presupuestos', leadId],
    queryFn: () => firebase.entities.Presupuesto.filter({ lead_id: leadId }, '-created_date'),
    enabled: !!leadId,
  });

  // Obtener emails del lead para mantener el hilo
  const { data: emails = [] } = useQuery({
    queryKey: ['emails', leadId],
    queryFn: () => firebase.entities.EmailMessage.filter({ lead_id: leadId }, '-date'),
    enabled: !!leadId,
  });

  const cargarPresupuesto = (presupuesto) => {
    console.log('Función cargarPresupuesto iniciada con:', presupuesto);
    
    if (!presupuesto || !presupuesto.lineas || presupuesto.lineas.length === 0) {
      console.log('Presupuesto vacío o sin líneas');
      return;
    }

    setPresupuestoActual(presupuesto);
    
    const lineasAgrupadas = {};
    presupuesto.lineas.forEach(lineaData => {
      const key = `${lineaData.producto_nombre}_${lineaData.descripcion || ''}_${lineaData.categoria_nombre}_${lineaData.margen}_${lineaData.coste_gestion}_${lineaData.proveedor || ''}`;
      
      if (!lineasAgrupadas[key]) {
        const categoria = categorias.find(c => c.nombre === lineaData.categoria_nombre);
        const producto = productos.find(p => p.nombre === lineaData.producto_nombre);
        
        lineasAgrupadas[key] = {
          id: Date.now() + Math.random(),
          categoria_id: categoria?.id || "",
          producto_id: producto ? producto.id : (lineaData.producto_nombre ? "puntual" : ""),
          producto_nombre: lineaData.producto_nombre || "",
          descripcion: lineaData.descripcion || "",
          es_puntual: !producto && !!lineaData.producto_nombre,
          margen: lineaData.margen !== null && lineaData.margen !== undefined ? parseFloat(lineaData.margen) : 0,
          coste_gestion: lineaData.coste_gestion !== null && lineaData.coste_gestion !== undefined ? parseFloat(lineaData.coste_gestion) : 0,
          proveedor: lineaData.proveedor || "",
          sub_lineas: []
        };
      }
      
      lineasAgrupadas[key].sub_lineas.push({
        cantidad: lineaData.cantidad,
        coste_proveedor: lineaData.coste_proveedor,
        coste_extra: lineaData.coste_extra || "",
        precio_venta: lineaData.precio_venta,
        precio_unitario: lineaData.precio_unitario,
        beneficio: lineaData.beneficio,
        usuario_edicion: lineaData.usuario_edicion
      });
    });

    // Completar con 3 sub_lineas mínimo
    Object.values(lineasAgrupadas).forEach(linea => {
      while (linea.sub_lineas.length < 3) {
        linea.sub_lineas.push({
          cantidad: "",
          coste_proveedor: "",
          coste_extra: "",
          precio_venta: 0,
          precio_unitario: 0,
          beneficio: 0,
          usuario_edicion: ""
        });
      }
    });

    const nuevasLineas = Object.values(lineasAgrupadas);
    console.log('Líneas cargadas:', nuevasLineas);
    setLineas(nuevasLineas);
  };

  // Cargar presupuesto específico desde el historial
  useEffect(() => {
    if (!presupuestoParaCargar || categorias.length === 0 || productos.length === 0) return;
    console.log('Cargando presupuesto desde historial:', presupuestoParaCargar);
    cargarPresupuesto(presupuestoParaCargar);
  }, [presupuestoParaCargar, categorias.length, productos.length]);

  // Cargar borrador automático solo al inicio
  useEffect(() => {
    if (categorias.length === 0 || productos.length === 0) return;
    if (presupuestos.length === 0) return;
    if (lineas.length > 0) return; // Ya hay líneas cargadas
    if (presupuestoActual) return; // Ya hay un presupuesto cargado
    
    const borrador = presupuestos.find(p => p.estado === 'borrador');
    if (borrador) {
      console.log('Cargando borrador automático:', borrador);
      cargarPresupuesto(borrador);
    }
  }, [presupuestos.length, categorias.length, productos.length, lineas.length, presupuestoActual]);

  const crearPresupuestoMutation = useMutation({
    mutationFn: async (data) => {
      const user = await firebase.auth.me();
      return firebase.entities.Presupuesto.create({ ...data, ultimo_usuario_edicion: user.email });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['presupuestos', leadId] });
      setPresupuestoActual(data);
    },
  });

  const actualizarPresupuestoMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      const user = await firebase.auth.me();
      return firebase.entities.Presupuesto.update(id, { ...data, ultimo_usuario_edicion: user.email });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['presupuestos', leadId] });
      setPresupuestoActual(data);
    },
  });



  const getIniciales = (email) => {
    if (!email) return null;
    const nombre = email.split('@')[0];
    return nombre.charAt(0).toUpperCase();
  };

  const guardarBorrador = async () => {
    // Filtrar líneas que tengan al menos una sub_linea con cantidad O coste
    const lineasValidas = lineas.filter(l => 
      l.categoria_id || l.producto_nombre || l.sub_lineas.some(sub => sub.cantidad || sub.coste_proveedor)
    );

    if (lineasValidas.length === 0) {
      toast({
        title: "Sin datos",
        description: "Agrega al menos una línea con datos",
        variant: "destructive"
      });
      return;
    }

    setSaving(true);
    try {
      const presupuestoData = {
        lead_id: leadId || null,
        tipo: leadId ? 'lead' : 'independiente',
        cliente: !leadId ? {
          nombre: lead?.nombre || "",
          empresa: lead?.empresa || "",
          email: lead?.email || "",
          telefono: lead?.telefono || "",
        } : null,
        numero_presupuesto: presupuestoActual?.numero_presupuesto || `P-${Date.now()}`,
        lineas: lineasValidas.flatMap(l => {
          const subLineasConDatos = l.sub_lineas.filter(sub => sub.cantidad || sub.coste_proveedor);
          if (subLineasConDatos.length === 0) {
            // Guardar al menos una línea vacía para mantener el producto
            return [{
              producto_nombre: l.producto_nombre || "",
              descripcion: l.descripcion || "",
              categoria_nombre: categorias.find(c => c.id === l.categoria_id)?.nombre || "",
              cantidad: 0,
              coste_proveedor: 0,
              margen: parseFloat(l.margen) || 0,
              coste_gestion: parseFloat(l.coste_gestion) || 0,
              proveedor: l.proveedor || "",
              precio_venta: 0,
              precio_unitario: 0,
              beneficio: 0,
              margen_euros: 0,
              margen_real_pct: 0,
              usuario_edicion: currentUserEmail
            }];
          }
          return subLineasConDatos.map(sub => {
            const precioVenta = parseFloat(sub.precio_venta) || 0;
            const costeProv = parseFloat(sub.coste_proveedor) || 0;
            const margenEuros = precioVenta - costeProv;
            const margenRealPct = precioVenta > 0 ? (margenEuros / precioVenta) * 100 : 0;
            return {
              producto_nombre: l.producto_nombre || "",
              descripcion: l.descripcion || "",
              categoria_nombre: categorias.find(c => c.id === l.categoria_id)?.nombre || "",
              cantidad: parseFloat(sub.cantidad) || 0,
              coste_proveedor: costeProv,
              coste_extra: parseFloat(sub.coste_extra) || 0,
              margen: parseFloat(l.margen) || 0,
              coste_gestion: parseFloat(l.coste_gestion) || 0,
              proveedor: l.proveedor || "",
              precio_venta: precioVenta,
              precio_unitario: parseFloat(sub.precio_unitario) || 0,
              beneficio: parseFloat(sub.beneficio) || 0,
              margen_euros: margenEuros,
              margen_real_pct: margenRealPct,
              usuario_edicion: sub.usuario_edicion || currentUserEmail
            };
          });
        }),
        total_presupuesto: calcularTotal(),
        total_beneficio: calcularBeneficioTotal(),
        texto_presupuesto: generarTextoPresupuesto(),
        estado: "borrador"
      };

      let resultado;
      if (presupuestoActual) {
        resultado = await actualizarPresupuestoMutation.mutateAsync({
          id: presupuestoActual.id,
          data: presupuestoData
        });
      } else {
        resultado = await crearPresupuestoMutation.mutateAsync(presupuestoData);
      }
      
      setPresupuestoActual(resultado);

      // Actualizar importe del lead (suma de precios de venta con cantidad más baja)
      // Solo cuando el presupuesto está vinculado a un lead.
      if (leadId) {
        const importeLead = calcularTotal();
        await firebase.entities.Lead.update(leadId, { importe: importeLead });
        queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
        queryClient.invalidateQueries({ queryKey: ['leads'] });
      }
      
      toast({
        title: "Guardado",
        description: "Presupuesto guardado correctamente",
      });
    } catch (error) {
      console.error('Error guardando borrador:', error);
      toast({
        title: "Error",
        description: "No se pudo guardar el presupuesto: " + error.message,
        variant: "destructive"
      });
    }
    setSaving(false);
  };

  const agregarLinea = () => {
    setLineas([...lineas, {
      id: Date.now(),
      categoria_id: "",
      producto_id: "",
      producto_nombre: "",
      descripcion: "",
      es_puntual: false,
      margen: 0,
      coste_gestion: 0,
      proveedor: "",
      sub_lineas: [
        { cantidad: "", coste_proveedor: "", coste_extra: "", precio_venta: 0, precio_unitario: 0, beneficio: 0, usuario_edicion: "" },
        { cantidad: "", coste_proveedor: "", coste_extra: "", precio_venta: 0, precio_unitario: 0, beneficio: 0, usuario_edicion: "" },
        { cantidad: "", coste_proveedor: "", coste_extra: "", precio_venta: 0, precio_unitario: 0, beneficio: 0, usuario_edicion: "" }
      ]
    }]);
  };

  const eliminarLinea = (id) => {
    setLineas(lineas.filter(l => l.id !== id));
  };

  const actualizarLinea = (id, campo, valor) => {
    setLineas(lineas.map(linea => {
      if (linea.id !== id) return linea;
      
      const nuevaLinea = { ...linea, [campo]: valor };

      if (campo === 'categoria_id') {
        const categoria = categorias.find(c => c && c.id === valor);
        if (categoria && !linea.categoria_id) {
          nuevaLinea.margen = categoria.margen_defecto || 0;
          nuevaLinea.coste_gestion = categoria.coste_fijo_defecto || 0;
        }
        nuevaLinea.producto_id = "";
        nuevaLinea.producto_nombre = "";
        nuevaLinea.es_puntual = false;
      }

      if (campo === 'producto_id') {
        if (valor === 'puntual') {
          nuevaLinea.es_puntual = true;
          nuevaLinea.producto_nombre = "";
          nuevaLinea.descripcion = "";
        } else if (valor) {
          const producto = productos.find(p => p && p.id === valor);
          if (producto) {
            nuevaLinea.producto_nombre = producto.nombre || "";
            nuevaLinea.descripcion = producto.descripcion || "";
            nuevaLinea.es_puntual = false;
            
            if (producto.margen_propio !== null && producto.margen_propio !== undefined) {
              nuevaLinea.margen = producto.margen_propio;
            }
            if (producto.coste_fijo_propio !== null && producto.coste_fijo_propio !== undefined) {
              nuevaLinea.coste_gestion = producto.coste_fijo_propio;
            }
          }
        }
      }

      if (campo === 'margen' || campo === 'coste_gestion') {
        setTimeout(() => {
          recalcularSubLineas(id);
        }, 0);
      }

      return nuevaLinea;
    }));
  };

  const actualizarSubLinea = (lineaId, subIndex, campo, valor) => {
    setLineas(lineas.map(linea => {
      if (linea.id !== lineaId) return linea;
      
      const nuevasSubLineas = [...linea.sub_lineas];
      nuevasSubLineas[subIndex] = { ...nuevasSubLineas[subIndex], [campo]: valor, usuario_edicion: currentUserEmail };

      const subLinea = nuevasSubLineas[subIndex];
      const coste = parseFloat(subLinea.coste_proveedor) || 0;
      const costeExtra = parseFloat(subLinea.coste_extra) || 0;
      const costeTotal = coste + costeExtra;
      const cantidad = parseFloat(subLinea.cantidad) || 1;
      const margen = parseFloat(linea.margen) || 0;
      const gestion = parseFloat(linea.coste_gestion) || 0;

      const factor = margen === 0 ? 1 : (1 - margen / 100);
      subLinea.precio_venta = (costeTotal / factor) + gestion;
      subLinea.precio_unitario = subLinea.precio_venta / cantidad;
      subLinea.beneficio = subLinea.precio_venta - costeTotal - gestion;

      return { ...linea, sub_lineas: nuevasSubLineas };
    }));
  };

  const recalcularSubLineas = (lineaId) => {
    setLineas(prevLineas => prevLineas.map(linea => {
      if (linea.id !== lineaId) return linea;
      
      const nuevasSubLineas = linea.sub_lineas.map(subLinea => {
        if (!subLinea.cantidad || !subLinea.coste_proveedor) return subLinea;
        
        const coste = parseFloat(subLinea.coste_proveedor) || 0;
        const costeExtra = parseFloat(subLinea.coste_extra) || 0;
        const costeTotal = coste + costeExtra;
        const cantidad = parseFloat(subLinea.cantidad) || 1;
        const margen = parseFloat(linea.margen) || 0;
        const gestion = parseFloat(linea.coste_gestion) || 0;

        const factor = margen === 0 ? 1 : (1 - margen / 100);
        const precio_venta = (costeTotal / factor) + gestion;
        const precio_unitario = precio_venta / cantidad;
        const beneficio = precio_venta - costeTotal - gestion;

        return { ...subLinea, precio_venta, precio_unitario, beneficio };
      });

      return { ...linea, sub_lineas: nuevasSubLineas };
    }));
  };

  const calcularTotal = () => {
    return lineas.reduce((sum, linea) => {
      // Solo usar la primera sub_linea con datos (cantidad más baja)
      const primeraSubLinea = linea.sub_lineas.find(sub => sub.cantidad && sub.coste_proveedor);
      if (primeraSubLinea) {
        return sum + (parseFloat(primeraSubLinea.precio_venta) || 0);
      }
      return sum;
    }, 0);
  };

  const calcularBeneficioTotal = () => {
    return lineas.reduce((sum, linea) => {
      // Solo usar la primera sub_linea con datos (cantidad más baja)
      const primeraSubLinea = linea.sub_lineas.find(sub => sub.cantidad && sub.coste_proveedor);
      if (primeraSubLinea) {
        return sum + (parseFloat(primeraSubLinea.beneficio) || 0);
      }
      return sum;
    }, 0);
  };

  const calcularCosteTotal = () => {
    return lineas.reduce((sum, linea) => {
      const primeraSubLinea = linea.sub_lineas.find(sub => sub.cantidad && sub.coste_proveedor);
      if (primeraSubLinea) {
        return sum + (parseFloat(primeraSubLinea.coste_proveedor) || 0);
      }
      return sum;
    }, 0);
  };

  const calcularMargenTotal = () => calcularTotal() - calcularCosteTotal();

  const calcularMargenMedioPct = () => {
    const total = calcularTotal();
    if (total <= 0) return 0;
    return (calcularMargenTotal() / total) * 100;
  };

  const formatNumero = (num) => {
    return num.toFixed(2).replace('.', ',');
  };

  const generarTextoPresupuesto = () => {
    const nombre = lead?.nombre || "";

    // Usar plantilla de ajustes o texto por defecto
    let texto = plantillaPresupuesto 
      ? plantillaPresupuesto.replace('{nombre}', nombre) + '\n\n'
      : `Hola ${nombre}\n\nTe indico los precios solicitados a continuación:\n\n`;

    // Iterar por cada línea (cada producto) por separado
    lineas.forEach(linea => {
      const subLineasConDatos = linea.sub_lineas.filter(sub => sub.cantidad && sub.coste_proveedor);
      if (subLineasConDatos.length === 0) return;

      // Título del producto
      const nombreProducto = linea.producto_nombre || categorias.find(c => c.id === linea.categoria_id)?.nombre || "Producto";
      texto += `${nombreProducto}\n`;
      if (linea.descripcion) {
        texto += `${linea.descripcion}\n`;
      }
      texto += `\n`;

      // Formato simple con separadores claros
      subLineasConDatos.forEach(sub => {
        const cantidad = parseInt(sub.cantidad);
        const precioSinIva = sub.precio_venta;
        const precioUnitario = sub.precio_unitario;
        const precioConIva = precioSinIva * 1.21;

        texto += `• ${cantidad} uds → ${formatNumero(precioSinIva)} € (${formatNumero(precioUnitario)} €/u) | Con IVA: ${formatNumero(precioConIva)} €\n`;
      });

      texto += `\n`;
    });

    texto += `─────────────────────────────────\n\n`;
    texto += `• Diseño o edición de archivos no incluido\n`;
    texto += `• Plazo de entrega estándar 7 días laborables (hacemos urgencias con suplemento de precio)\n`;
    texto += `• Envío incluido (excepto islas)\n\n`;
    texto += `Si te encaja y quieres poner en marcha sigue los siguientes pasos:\n\n`;
    texto += `1) REALIZAR PAGO, con tarjeta o transferencia en https://roig.net/pago\n`;
    texto += `2) ENVIAR ARCHIVOS PARA IMPRIMIR\n`;
    texto += `3) INDÍCANOS TUS DATOS COMPLETOS, para el envío y la factura\n\n`;
    
    // Usar firma de ajustes o texto por defecto
    texto += firmaEmail || `Un saludo`;

    return texto;
  };

  const handleCopiarTexto = () => {
    navigator.clipboard.writeText(textoPresupuesto);
    setTextoCopied(true);
    setTimeout(() => setTextoCopied(false), 2000);
    toast({
      title: "Texto copiado",
      description: "El presupuesto se ha copiado al portapapeles",
    });
  };

  const handleImprimir = () => {
    const win = window.open("", "_blank", "width=900,height=1100");
    if (!win) {
      toast({
        title: "Bloqueo de pop-ups",
        description: "Permite las ventanas emergentes para este sitio e inténtalo de nuevo.",
        variant: "destructive",
      });
      return;
    }
    win.document.write(generarHtmlImpresion());
    win.document.close();
  };

  const escapeHtml = (s) => String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

  const generarHtmlImpresion = () => {
    const numero = presupuestoActual?.numero_presupuesto || "BORRADOR";
    const fecha = new Date().toLocaleDateString("es-ES", { year: "numeric", month: "long", day: "numeric" });

    // Solo líneas con al menos una sub_linea con datos
    const lineasImprimibles = lineas
      .map(linea => ({
        ...linea,
        sub_lineas_validas: linea.sub_lineas.filter(s => s.cantidad && s.coste_proveedor),
      }))
      .filter(l => l.sub_lineas_validas.length > 0);

    const subtotal = lineasImprimibles.reduce((acc, l) => {
      const primera = l.sub_lineas_validas[0];
      return acc + (parseFloat(primera.precio_venta) || 0);
    }, 0);
    const iva = subtotal * 0.21;
    const totalConIva = subtotal + iva;

    const fmt = (n) => (parseFloat(n) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const filasHtml = lineasImprimibles.map(linea => {
      const nombreProducto = linea.producto_nombre
        || categorias.find(c => c.id === linea.categoria_id)?.nombre
        || "Producto";
      return linea.sub_lineas_validas.map((sub, idx) => {
        const cantidad = parseInt(sub.cantidad, 10);
        const unitario = parseFloat(sub.precio_unitario) || 0;
        const total = parseFloat(sub.precio_venta) || 0;
        const productoCell = idx === 0
          ? `<td class="prod"><strong>${escapeHtml(nombreProducto)}</strong>${linea.descripcion ? `<div class="desc">${escapeHtml(linea.descripcion)}</div>` : ""}</td>`
          : `<td class="prod"><span class="opcion">Opción ${idx + 1}</span></td>`;
        return `<tr>
          ${productoCell}
          <td class="num">${cantidad}</td>
          <td class="num">${fmt(unitario)} €</td>
          <td class="num">${fmt(total)} €</td>
        </tr>`;
      }).join("");
    }).join("");

    const cliente = lead || {};

    return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Presupuesto ${escapeHtml(numero)} — Estudi Roig</title>
<style>
  @page { size: A4; margin: 18mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif; color: #1f2937; margin: 0; padding: 24px; font-size: 12px; line-height: 1.5; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #1f2937; padding-bottom: 16px; margin-bottom: 24px; }
  .brand { font-size: 28px; font-weight: 700; letter-spacing: 2px; color: #1f2937; }
  .empresa-meta { text-align: right; font-size: 11px; color: #4b5563; line-height: 1.6; }
  .doc-meta { display: flex; justify-content: space-between; margin-bottom: 24px; gap: 24px; }
  .box { border: 1px solid #e5e7eb; padding: 12px 14px; border-radius: 6px; flex: 1; }
  .box h3 { margin: 0 0 8px 0; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #6b7280; font-weight: 600; }
  .box p { margin: 2px 0; }
  table.lineas { width: 100%; border-collapse: collapse; margin-bottom: 16px; }
  table.lineas thead th { background: #f3f4f6; padding: 8px 10px; text-align: left; font-size: 10px; text-transform: uppercase; letter-spacing: 1px; color: #374151; border-bottom: 2px solid #1f2937; }
  table.lineas th.num, table.lineas td.num { text-align: right; }
  table.lineas td { padding: 10px; border-bottom: 1px solid #e5e7eb; vertical-align: top; }
  table.lineas td.prod { width: 50%; }
  table.lineas td .desc { font-size: 11px; color: #6b7280; margin-top: 4px; white-space: pre-wrap; }
  table.lineas td .opcion { font-size: 11px; color: #9ca3af; font-style: italic; }
  .totales { margin-left: auto; width: 45%; }
  .totales table { width: 100%; border-collapse: collapse; }
  .totales td { padding: 6px 10px; }
  .totales tr.subtotal td { border-top: 1px solid #e5e7eb; }
  .totales tr.total td { border-top: 2px solid #1f2937; font-size: 14px; font-weight: 700; padding-top: 10px; }
  .totales td.label { color: #4b5563; }
  .totales td.amount { text-align: right; }
  .condiciones { margin-top: 28px; padding-top: 16px; border-top: 1px solid #e5e7eb; font-size: 11px; color: #4b5563; }
  .condiciones h4 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #374151; margin: 0 0 8px 0; }
  .condiciones ul { margin: 0; padding-left: 18px; }
  .condiciones li { margin-bottom: 4px; }
  .footer { margin-top: 28px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; text-align: center; }
  @media print {
    body { padding: 0; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
  <div class="header">
    <div>
      <div class="brand">ESTUDI ROIG</div>
      <div style="font-size: 11px; color: #6b7280; margin-top: 4px;">Imprenta y artes gráficas</div>
    </div>
    <div class="empresa-meta">
      <div><strong>Estudi Roig</strong></div>
      <div>Tel. 934 329 822</div>
      <div>info@estudiroig.com</div>
    </div>
  </div>

  <div class="doc-meta">
    <div class="box">
      <h3>Cliente</h3>
      ${cliente.nombre ? `<p><strong>${escapeHtml(cliente.nombre)}</strong></p>` : ""}
      ${cliente.empresa ? `<p>${escapeHtml(cliente.empresa)}</p>` : ""}
      ${cliente.email ? `<p>${escapeHtml(cliente.email)}</p>` : ""}
      ${cliente.telefono ? `<p>${escapeHtml(cliente.telefono)}</p>` : ""}
    </div>
    <div class="box">
      <h3>Presupuesto</h3>
      <p><strong>Nº ${escapeHtml(numero)}</strong></p>
      <p>Fecha: ${escapeHtml(fecha)}</p>
      <p>Validez: 30 días</p>
    </div>
  </div>

  <table class="lineas">
    <thead>
      <tr>
        <th>Producto</th>
        <th class="num">Cantidad</th>
        <th class="num">Precio unitario</th>
        <th class="num">Total</th>
      </tr>
    </thead>
    <tbody>
      ${filasHtml || `<tr><td colspan="4" style="text-align:center; color:#9ca3af; padding:20px;">Sin líneas</td></tr>`}
    </tbody>
  </table>

  <div class="totales">
    <table>
      <tr class="subtotal"><td class="label">Subtotal</td><td class="amount">${fmt(subtotal)} €</td></tr>
      <tr><td class="label">IVA (21%)</td><td class="amount">${fmt(iva)} €</td></tr>
      <tr class="total"><td class="label">Total</td><td class="amount">${fmt(totalConIva)} €</td></tr>
    </table>
  </div>

  <div class="condiciones">
    <h4>Condiciones</h4>
    <ul>
      <li>Diseño o edición de archivos no incluido.</li>
      <li>Plazo de entrega estándar 7 días laborables (urgencias con suplemento de precio).</li>
      <li>Envío incluido (excepto islas).</li>
      <li>Precios sin IVA salvo indicación. IVA aplicable: 21%.</li>
      <li>Presupuesto válido durante 30 días desde la fecha de emisión.</li>
    </ul>
  </div>

  <div class="footer">Estudi Roig · 934 329 822 · info@estudiroig.com</div>

  <script>
    setTimeout(function() { window.print(); }, 500);
  </script>
</body>
</html>`;
  };

  const handleEnviarEmail = async () => {
    setIsSending(true);
    try {
      await guardarBorrador();
      
      if (presupuestoActual) {
        await actualizarPresupuestoMutation.mutateAsync({
          id: presupuestoActual.id,
          data: {
            estado: "enviado_email",
            fecha_envio_email: new Date().toISOString(),
            bloqueado: true
          }
        });
      }

      // Cambiar estado del lead a presupuesto_enviado
      await firebase.entities.Lead.update(leadId, { estado: "presupuesto_enviado" });
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });

      // Buscar el último email para mantener el hilo
      const lastEmail = emails.length > 0 ? emails[0] : null;
      const emailSubject = lastEmail 
        ? (lastEmail.subject?.startsWith('Re:') ? lastEmail.subject : `Re: ${lastEmail.subject}`)
        : "Presupuesto Estudi Roig";

      await firebase.functions.invoke('sendEmailIMAP', {
        lead_id: leadId,
        to_email: lead.email,
        subject: emailSubject,
        body: textoPresupuesto,
        in_reply_to: lastEmail?.message_id || null,
        references: lastEmail?.message_id || null
      });

      // Re-invalidar tras el envío para recoger el pendiente_email=false
      // que el backend deja en sendEmailIMAP (si solo invalidamos antes,
      // el refetch corre en paralelo y se queda con el estado viejo).
      queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
      queryClient.invalidateQueries({ queryKey: ['leads'] });

      // Crear seguimiento automático si está activado
      if (seguimientoAutomatico && presupuestoActual) {
        try {
          await firebase.entities.SeguimientoEmail.create({
            lead_id: leadId,
            presupuesto_id: presupuestoActual.id,
            activo: true,
            fecha_envio_presupuesto: new Date().toISOString(),
            emails_enviados: [],
            cancelado_por_respuesta: false
          });
          queryClient.invalidateQueries({ queryKey: ['seguimientos', leadId] });
        } catch (error) {
          console.error('Error creando seguimiento automático:', error);
        }
      }

      try {
        await firebase.functions.invoke('sendDiscordAlert', {
          evento: 'presupuesto_email',
          lead_id: leadId,
          datos_extra: {
            numero_presupuesto: presupuestoActual?.numero_presupuesto,
            total: calcularTotal().toFixed(2),
            seguimiento_automatico: seguimientoAutomatico
          }
        });
      } catch (error) {
        console.error('Error enviando alerta Discord:', error);
      }

      toast({
        title: "Email enviado",
        description: seguimientoAutomatico 
          ? "El presupuesto se ha enviado. Seguimiento automático activado."
          : "El presupuesto se ha enviado correctamente",
      });

      queryClient.invalidateQueries({ queryKey: ['presupuestos', leadId] });
      
      // Mostrar diálogo para crear actividad de seguimiento
      setShowActividadDialog(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo enviar el email",
        variant: "destructive"
      });
    }
    setIsSending(false);
  };

  const handleCrearActividad = async (actividadData) => {
    setCreandoActividad(true);
    try {
      await firebase.entities.Actividad.create({
        lead_id: leadId,
        ...actividadData
      });
      queryClient.invalidateQueries({ queryKey: ['actividades'] });
      setShowActividadDialog(false);
      toast({
        title: "Recordatorio creado",
        description: "Se ha programado el seguimiento",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo crear el recordatorio",
        variant: "destructive"
      });
    }
    setCreandoActividad(false);
  };

  const limpiarPresupuesto = () => {
    setLineas([]);
    setPresupuestoActual(null);
  };

  const getProductosFiltrados = (categoriaId) => {
    if (!categoriaId) return [];
    return productos.filter(p => p && p.categoria_id === categoriaId) || [];
  };

  const handleAbrirCrearProducto = (lineaId, categoriaId) => {
    setCategoriaParaNuevoProducto(categoriaId);
    setLineaParaNuevoProducto(lineaId);
    setShowProductoDialog(true);
  };

  const handleToggleBloqueo = async () => {
    if (!presupuestoActual) return;
    
    const nuevoEstado = !presupuestoActual.bloqueado;
    await actualizarPresupuestoMutation.mutateAsync({
      id: presupuestoActual.id,
      data: { bloqueado: nuevoEstado }
    });
    
    toast({
      title: nuevoEstado ? "Presupuesto bloqueado" : "Presupuesto desbloqueado",
      description: nuevoEstado 
        ? "El presupuesto no se puede editar" 
        : "Ahora puedes editar el presupuesto",
    });
  };

  const handleCrearProducto = async (productoData) => {
    setCreandoProducto(true);
    try {
      const nuevoProducto = await firebase.entities.Producto.create(productoData);
      queryClient.invalidateQueries({ queryKey: ['productos'] });

      // Seleccionar automáticamente el nuevo producto en la línea
      if (lineaParaNuevoProducto) {
        setTimeout(() => {
          actualizarLinea(lineaParaNuevoProducto, 'producto_id', nuevoProducto.id);
        }, 100);
      }

      setShowProductoDialog(false);
      toast({
        title: "Producto creado",
        description: "El producto se ha creado correctamente",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "No se pudo crear el producto",
        variant: "destructive"
      });
    }
    setCreandoProducto(false);
  };

  const handleTruyolPrecios = (lineaId, preciosTruyol, nombreProducto, plazo) => {
    setLineas(prevLineas => prevLineas.map(linea => {
      if (linea.id !== lineaId) return linea;

      // Rellenar sub_líneas con los precios de Truyol
      const nuevasSubLineas = preciosTruyol.map(p => {
        const coste = p.coste_proveedor;
        const cantidad = p.cantidad;
        const margen = parseFloat(linea.margen) || 0;
        const gestion = parseFloat(linea.coste_gestion) || 0;
        const factor = margen === 0 ? 1 : (1 - margen / 100);
        const precio_venta = (coste / factor) + gestion;
        const precio_unitario = precio_venta / (cantidad || 1);
        const beneficio = precio_venta - coste - gestion;

        return {
          cantidad,
          coste_proveedor: coste,
          coste_extra: "",
          precio_venta,
          precio_unitario,
          beneficio,
          usuario_edicion: currentUserEmail,
        };
      });

      // Completar hasta 3 sub_líneas mínimo
      while (nuevasSubLineas.length < 3) {
        nuevasSubLineas.push({ cantidad: "", coste_proveedor: "", coste_extra: "", precio_venta: 0, precio_unitario: 0, beneficio: 0, usuario_edicion: "" });
      }

      const descripcionPlazo = `Plazo: ${plazo}`;
      return {
        ...linea,
        producto_nombre: linea.producto_nombre || nombreProducto,
        descripcion: linea.descripcion || descripcionPlazo,
        proveedor: linea.proveedor || "Truyol",
        sub_lineas: nuevasSubLineas,
      };
    }));

    setTruyolAbierto(null);
    toast({ title: "Precios cargados", description: `${preciosTruyol.length} cantidades desde Truyol (${plazo})` });
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CardTitle className="text-gray-900 dark:text-white">
              Generar Presupuesto
              {presupuestoActual && (
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  ({presupuestoActual.numero_presupuesto})
                </span>
              )}
            </CardTitle>
            {presupuestoActual?.ultimo_usuario_edicion && (
              <div className="w-7 h-7 rounded-full bg-indigo-500 text-white flex items-center justify-center text-xs font-semibold">
                {getIniciales(presupuestoActual.ultimo_usuario_edicion)}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {presupuestoActual && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleToggleBloqueo}
                className={presupuestoActual.bloqueado ? "text-orange-600 border-orange-300" : "text-green-600 border-green-300"}
              >
                {presupuestoActual.bloqueado ? (
                  <>
                    <Lock className="w-3 h-3 mr-1" />
                    Bloqueado
                  </>
                ) : (
                  <>
                    <Unlock className="w-3 h-3 mr-1" />
                    Desbloqueado
                  </>
                )}
              </Button>
            )}
            {lineas.length > 0 && !presupuestoActual?.bloqueado && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={guardarBorrador}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    Guardando...
                  </>
                ) : (
                  "Guardar Presupuesto"
                )}
              </Button>
            )}
            {presupuestoActual && lineas.length > 0 && (
              <Button variant="outline" size="sm" onClick={limpiarPresupuesto}>
                Nuevo Presupuesto
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {presupuestoActual?.bloqueado && (
            <div className="p-3 bg-orange-50 dark:bg-orange-950 rounded-lg border border-orange-200 dark:border-orange-800 flex items-center gap-2 text-orange-700 dark:text-orange-300">
              <Lock className="w-4 h-4" />
              <span className="text-sm">Presupuesto bloqueado. Haz clic en "Bloqueado" para desbloquear y editar.</span>
            </div>
          )}

          {lineas.map((linea, index) => (
            <div key={linea.id} className={`p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4 bg-gray-50 dark:bg-gray-800 ${presupuestoActual?.bloqueado ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex justify-between items-center">
                <h4 className="font-semibold text-gray-900 dark:text-white">Línea {index + 1}</h4>
                <Button variant="ghost" size="sm" onClick={() => eliminarLinea(linea.id)} disabled={presupuestoActual?.bloqueado}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="dark:text-gray-300">Categoría</Label>
                  <select 
                    value={linea.categoria_id} 
                    onChange={(e) => actualizarLinea(linea.id, 'categoria_id', e.target.value)}
                    className="w-full h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Seleccionar categoría</option>
                    {categorias.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.nombre}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label className="dark:text-gray-300">Producto</Label>
                  <div className="flex gap-2">
                    <select 
                      value={linea.producto_id} 
                      onChange={(e) => actualizarLinea(linea.id, 'producto_id', e.target.value)}
                      disabled={!linea.categoria_id}
                      className="flex-1 h-10 px-3 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white text-sm disabled:opacity-50"
                    >
                      <option value="">Seleccionar producto</option>
                      <option value="puntual">➕ Producto puntual</option>
                      {getProductosFiltrados(linea.categoria_id).map(prod => (
                        <option key={prod.id} value={prod.id}>{prod.nombre}</option>
                      ))}
                    </select>
                    {linea.producto_id && linea.producto_id !== 'puntual' && (() => {
                      const prod = productos.find(p => p.id === linea.producto_id);
                      return prod?.url_proveedor ? (
                        <a
                          href={prod.url_proveedor}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                          title="Ver en web del proveedor"
                        >
                          <ExternalLink className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                        </a>
                      ) : null;
                    })()}
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      disabled={!linea.categoria_id}
                      onClick={() => handleAbrirCrearProducto(linea.id, linea.categoria_id)}
                      title="Crear nuevo producto"
                      className="h-10 w-10 shrink-0"
                    >
                      <PlusCircle className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>

              {linea.es_puntual && (
                <div>
                  <Label className="dark:text-gray-300">Nombre del producto</Label>
                  <Input
                    value={linea.producto_nombre}
                    onChange={(e) => actualizarLinea(linea.id, 'producto_nombre', e.target.value)}
                    placeholder="Nombre del producto puntual"
                    className="dark:bg-gray-900 dark:border-gray-700"
                  />
                </div>
              )}

              {(linea.es_puntual || linea.producto_id) && (
                <div>
                  <Label className="dark:text-gray-300">Descripción</Label>
                  <Textarea
                    value={linea.descripcion}
                    onChange={(e) => actualizarLinea(linea.id, 'descripcion', e.target.value)}
                    placeholder="Descripción del producto"
                    className="dark:bg-gray-900 dark:border-gray-700"
                    rows={2}
                  />
                </div>
              )}

              {isAdmin && (
                <div className="grid grid-cols-3 gap-4 bg-blue-50 dark:bg-blue-950 p-3 rounded">
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Margen (%)</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={linea.margen}
                      onChange={(e) => actualizarLinea(linea.id, 'margen', parseFloat(e.target.value) || 0)}
                      className="dark:bg-gray-900 dark:border-gray-700 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Coste Gestión (€)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={linea.coste_gestion}
                      onChange={(e) => actualizarLinea(linea.id, 'coste_gestion', parseFloat(e.target.value) || 0)}
                      className="dark:bg-gray-900 dark:border-gray-700 h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600 dark:text-gray-400">Proveedor</Label>
                    <Input
                      type="text"
                      value={linea.proveedor || ""}
                      onChange={(e) => actualizarLinea(linea.id, 'proveedor', e.target.value)}
                      placeholder="Truyol, propia, etc."
                      className="dark:bg-gray-900 dark:border-gray-700 h-9"
                    />
                  </div>
                </div>
              )}

              {/* Botón para abrir el selector de precios Truyol */}
              <div className="flex justify-end">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setTruyolAbierto(truyolAbierto === linea.id ? null : linea.id)}
                  className="text-xs text-blue-600 border-blue-200 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-700 dark:hover:bg-blue-950"
                >
                  🖨️ {truyolAbierto === linea.id ? "Cerrar Truyol" : "Buscar precio Truyol"}
                </Button>
              </div>

              {truyolAbierto === linea.id && (
                <TruyolSelector
                  catalogoProductos={catalogoTruyol}
                  catalogoMaestro={catalogoTruyolMaestro}
                  onPrecios={(precios, nombre, plazo) => handleTruyolPrecios(linea.id, precios, nombre, plazo)}
                  onClose={() => setTruyolAbierto(null)}
                />
              )}

              <div className="space-y-2">
                <div className="grid grid-cols-6 gap-2 text-xs font-medium text-gray-600 dark:text-gray-400">
                  <span>Cant.</span>
                  <span>Coste (€)</span>
                  <span>Extra (€)</span>
                  <span className="col-span-2"></span>
                  <span className="text-center">Usuario</span>
                </div>

                {linea.sub_lineas.map((subLinea, subIndex) => (
                  <div key={subIndex} className="grid grid-cols-6 gap-2 items-center">
                    <Input
                      type="number"
                      value={subLinea.cantidad}
                      onChange={(e) => actualizarSubLinea(linea.id, subIndex, 'cantidad', e.target.value)}
                      placeholder="0"
                      className="dark:bg-gray-900 dark:border-gray-700 text-sm h-9"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={subLinea.coste_proveedor}
                      onChange={(e) => actualizarSubLinea(linea.id, subIndex, 'coste_proveedor', e.target.value)}
                      placeholder="0.00"
                      className="dark:bg-gray-900 dark:border-gray-700 text-sm h-9"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={subLinea.coste_extra}
                      onChange={(e) => actualizarSubLinea(linea.id, subIndex, 'coste_extra', e.target.value)}
                      placeholder="0.00"
                      className="dark:bg-gray-900 dark:border-gray-700 text-sm h-9"
                    />
                    <div className="col-span-2 flex items-center gap-2 text-xs flex-wrap">
                      {subLinea.cantidad && subLinea.coste_proveedor && (
                        <>
                          <span className="text-gray-600 dark:text-gray-400">→</span>
                          <span className="font-bold text-indigo-600 dark:text-indigo-400">
                            {subLinea.precio_venta.toFixed(2)}€
                          </span>
                          <span className="text-gray-600 dark:text-gray-400">
                            ({subLinea.precio_unitario.toFixed(2)}€/u)
                          </span>
                          {isAdmin && (() => {
                            const margenEuros = (parseFloat(subLinea.precio_venta) || 0) - (parseFloat(subLinea.coste_proveedor) || 0);
                            const margenPct = subLinea.precio_venta > 0 ? (margenEuros / subLinea.precio_venta) * 100 : 0;
                            return (
                              <span className="text-emerald-600 dark:text-emerald-400 font-medium" title="Margen = precio_venta − coste_proveedor">
                                · {margenEuros.toFixed(2)}€ ({margenPct.toFixed(1)}%)
                              </span>
                            );
                          })()}
                        </>
                      )}
                    </div>
                    <div className="flex justify-center">
                      {subLinea.usuario_edicion && (
                        <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-semibold">
                          {getIniciales(subLinea.usuario_edicion)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {!presupuestoActual?.bloqueado && (
            <Button onClick={agregarLinea} variant="outline" className="w-full dark:border-gray-700 dark:text-gray-300">
              <Plus className="w-4 h-4 mr-2" />
              Agregar Línea
            </Button>
          )}

          {lineas.length > 0 && lineas.some(l => l.sub_lineas.some(sub => sub.cantidad && sub.coste_proveedor)) && (
            <>
              <div className="bg-indigo-50 dark:bg-indigo-950 p-6 rounded-lg">
                <div className={`grid ${isAdmin ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
                  <div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Presupuesto</p>
                    <p className="text-3xl font-bold text-indigo-600 dark:text-indigo-400">{calcularTotal().toFixed(2)}€</p>
                  </div>
                  {isAdmin && (
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Beneficio Total</p>
                      <p className="text-3xl font-bold text-green-600 dark:text-green-400">{calcularBeneficioTotal().toFixed(2)}€</p>
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-indigo-200 dark:border-indigo-800">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Total Coste Proveedor</p>
                      <p className="text-lg font-semibold text-gray-700 dark:text-gray-300">{calcularCosteTotal().toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Margen Total</p>
                      <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{calcularMargenTotal().toFixed(2)}€</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Margen Medio</p>
                      <p className="text-lg font-semibold text-emerald-600 dark:text-emerald-400">{calcularMargenMedioPct().toFixed(1)}%</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
                <div className="flex justify-between items-center mb-2">
                  <Label className="dark:text-gray-300">Vista previa del presupuesto</Label>
                  <Button variant="outline" size="sm" onClick={handleCopiarTexto}>
                    {textoCopied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        Copiar texto
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  value={textoPresupuesto}
                  onChange={(e) => setTextoPresupuesto(e.target.value)}
                  className="font-mono text-xs dark:bg-gray-900 dark:border-gray-700"
                  rows={15}
                />
              </div>

              <div className="space-y-3">
                {leadId && (
                  <div className="flex items-center gap-2 p-3 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
                    <Checkbox
                      id="seguimiento"
                      checked={seguimientoAutomatico}
                      onCheckedChange={setSeguimientoAutomatico}
                    />
                    <label
                      htmlFor="seguimiento"
                      className="flex items-center gap-2 text-sm text-blue-700 dark:text-blue-300 cursor-pointer flex-1"
                    >
                      <Bell className="w-4 h-4" />
                      Seguimiento automático
                      <span className="text-xs text-blue-500 dark:text-blue-400">
                        (emails a los 2 y 4 días si no responde)
                      </span>
                    </label>
                  </div>
                )}

                <div className="flex gap-3">
                  {!presupuestoActual?.bloqueado && (
                    <Button
                      variant="outline"
                      onClick={guardarBorrador}
                      disabled={saving}
                      className="flex-1"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Guardando...
                        </>
                      ) : (
                        "Guardar Presupuesto"
                      )}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    onClick={handleImprimir}
                    className="flex-1"
                  >
                    <Printer className="w-4 h-4 mr-2" />
                    Imprimir
                  </Button>
                  {leadId && (
                    <Button
                      onClick={handleEnviarEmail}
                      disabled={isSending || presupuestoActual?.bloqueado}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                    >
                      {isSending ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Mail className="w-4 h-4 mr-2" />
                          Enviar por Email
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </CardContent>
      <ProductoDialog
        open={showProductoDialog}
        onClose={() => setShowProductoDialog(false)}
        onSubmit={handleCrearProducto}
        producto={{ categoria_id: categoriaParaNuevoProducto }}
        categorias={categorias}
        isLoading={creandoProducto}
      />
      <CrearActividadDialog
        open={showActividadDialog}
        onClose={() => setShowActividadDialog(false)}
        onSubmit={handleCrearActividad}
        isLoading={creandoActividad}
      />
    </Card>
  );
});

PresupuestoGenerator.displayName = 'PresupuestoGenerator';

export default PresupuestoGenerator;

export { ProductoDialog };